import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    CardActions,
    Button,
    CircularProgress,
    Alert,
    AlertTitle,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import { API_BASE } from '../../apiBase';

export default function MaintenanceTab({ headers }) {
    const [tasks, setTasks] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [completeDialog, setCompleteDialog] = useState({ open: false, task: null, notes: '' });
    const [historyDialog, setHistoryDialog] = useState({ open: false, task: null, history: [], loading: false });
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        loadMaintenanceTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadMaintenanceTasks = async () => {
        try {
            setLoading(true);
            setError(null);

            // Load tasks and statistics in parallel
            const [tasksRes, statsRes] = await Promise.all([
                fetch(`${API_BASE}/admin/maintenance-tasks`, { headers }),
                fetch(`${API_BASE}/admin/maintenance-tasks/statistics`, { headers })
            ]);

            if (!tasksRes.ok || !statsRes.ok) {
                throw new Error('Failed to load maintenance tasks');
            }

            const tasksData = await tasksRes.json();
            const statsData = await statsRes.json();

            setTasks(tasksData.tasks || []);
            setStatistics(statsData.statistics || null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCompleteTask = async () => {
        const { task, notes } = completeDialog;
        if (!task) return;

        try {
            const res = await fetch(`${API_BASE}/admin/maintenance-tasks/${task.task_id}/complete`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ notes: notes || '' })
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to complete task');
            }

            setSuccessMessage(`Task "${task.name}" marked as completed`);
            setCompleteDialog({ open: false, task: null, notes: '' });

            // Reload tasks
            setTimeout(() => loadMaintenanceTasks(), 500);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleViewHistory = async (task) => {
        setHistoryDialog({ open: true, task, history: [], loading: true });

        try {
            const res = await fetch(
                `${API_BASE}/admin/maintenance-tasks/${task.task_id}/history?limit=20`,
                { headers }
            );

            if (!res.ok) {
                throw new Error('Failed to load history');
            }

            const data = await res.json();
            setHistoryDialog(prev => ({ ...prev, history: data.history || [], loading: false }));
        } catch (err) {
            setHistoryDialog(prev => ({ ...prev, loading: false }));
            setError(err.message);
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'Never';
        const date = new Date(isoString);
        return date.toLocaleString();
    };

    const getTaskStatusColor = (task) => {
        if (task.is_recently_completed) return 'success';
        if (task.is_overdue) return 'error';
        return 'default';
    };

    const getTaskStatusIcon = (task) => {
        if (task.is_recently_completed) return <CheckCircleIcon />;
        if (task.is_overdue) return <ErrorIcon />;
        return <WarningIcon />;
    };

    const getTaskStatusText = (task) => {
        if (task.is_recently_completed) return 'Recently Completed';
        if (task.is_overdue) {
            if (task.last_completed) {
                return `Overdue (${task.days_overdue} days)`;
            }
            return 'Never Completed';
        }
        if (task.last_completed) {
            return `Completed ${task.days_since_completion} days ago`;
        }
        return 'Not Yet Completed';
    };

    const groupTasksByFrequency = () => {
        const groups = {
            weekly: [],
            monthly: [],
            quarterly: [],
            annual: []
        };

        tasks.forEach(task => {
            if (groups[task.frequency]) {
                groups[task.frequency].push(task);
            }
        });

        return groups;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error">
                    <AlertTitle>Error Loading Maintenance Tasks</AlertTitle>
                    {error}
                </Alert>
                <Button startIcon={<RefreshIcon />} onClick={loadMaintenanceTasks} sx={{ mt: 2 }}>
                    Retry
                </Button>
            </Box>
        );
    }

    const taskGroups = groupTasksByFrequency();

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">Maintenance Tasks</Typography>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={loadMaintenanceTasks}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {/* ISDS Compliance Notice */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>ISDS Compliance: Recurring Maintenance Tasks</AlertTitle>
                Track completion of required maintenance activities as specified in ISDS requirements.
                Ensures demonstrable compliance with backup verification, security reviews, and access audits.
            </Alert>

            {/* Success Message */}
            {successMessage && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => setSuccessMessage(null)}
                >
                    {successMessage}
                </Alert>
            )}

            {/* Statistics Overview */}
            {statistics && (
                <Grid container spacing={2} mb={3}>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <AssignmentTurnedInIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Total Tasks</Typography>
                                </Box>
                                <Typography variant="h3">{statistics.total_tasks}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <AssignmentLateIcon color="error" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Overdue</Typography>
                                </Box>
                                <Typography variant="h3" color="error">
                                    {statistics.overdue_tasks}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Require attention
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Recently Done</Typography>
                                </Box>
                                <Typography variant="h3" color="success.main">
                                    {statistics.recently_completed}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Within last 7 days
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <WarningIcon color="warning" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Never Done</Typography>
                                </Box>
                                <Typography variant="h3" color="warning.main">
                                    {statistics.never_completed}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Need first completion
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Task Groups by Frequency */}
            {Object.entries(taskGroups).map(([frequency, frequencyTasks]) => {
                if (frequencyTasks.length === 0) return null;

                return (
                    <Accordion key={frequency} defaultExpanded={frequency === 'weekly' || frequency === 'monthly'}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6" textTransform="capitalize">
                                {frequency} Tasks ({frequencyTasks.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {frequencyTasks.map(task => (
                                    <Grid item xs={12} md={6} lg={4} key={task.task_id}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderColor: task.is_overdue ? 'error.main' :
                                                    task.is_recently_completed ? 'success.main' : 'divider',
                                                borderWidth: task.is_overdue || task.is_recently_completed ? 2 : 1
                                            }}
                                        >
                                            <CardContent sx={{ flexGrow: 1 }}>
                                                <Box display="flex" alignItems="flex-start" mb={1}>
                                                    <Box flexGrow={1}>
                                                        <Typography variant="h6" gutterBottom>
                                                            {task.name}
                                                        </Typography>
                                                        <Chip
                                                            size="small"
                                                            label={task.isds_reference}
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{ mr: 1 }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={getTaskStatusText(task)}
                                                            color={getTaskStatusColor(task)}
                                                            icon={getTaskStatusIcon(task)}
                                                        />
                                                    </Box>
                                                </Box>

                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                    {task.description}
                                                </Typography>

                                                <Divider sx={{ my: 1 }} />

                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Frequency:</strong> Every {task.interval_days} days
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Last Completed:</strong> {formatDate(task.last_completed)}
                                                </Typography>
                                                {task.last_completed_by && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>By:</strong> {task.last_completed_by}
                                                    </Typography>
                                                )}
                                                {task.last_notes && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                        "{task.last_notes}"
                                                    </Typography>
                                                )}
                                            </CardContent>

                                            <CardActions>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<CheckCircleIcon />}
                                                    onClick={() => setCompleteDialog({ open: true, task, notes: '' })}
                                                    fullWidth
                                                >
                                                    Mark as Complete
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<HistoryIcon />}
                                                    onClick={() => handleViewHistory(task)}
                                                >
                                                    History
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {/* Complete Task Dialog */}
            <Dialog
                open={completeDialog.open}
                onClose={() => setCompleteDialog({ open: false, task: null, notes: '' })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Mark Task as Complete
                </DialogTitle>
                <DialogContent>
                    {completeDialog.task && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {completeDialog.task.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                {completeDialog.task.description}
                            </Typography>
                            <Chip
                                size="small"
                                label={completeDialog.task.isds_reference}
                                color="primary"
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Completion Notes (Optional)"
                                multiline
                                rows={4}
                                value={completeDialog.notes}
                                onChange={(e) => setCompleteDialog({ ...completeDialog, notes: e.target.value })}
                                placeholder="Add any notes about this completion (e.g., findings, actions taken, issues encountered)"
                                sx={{ mt: 2 }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCompleteDialog({ open: false, task: null, notes: '' })}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleCompleteTask} startIcon={<CheckCircleIcon />}>
                        Mark Complete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History Dialog */}
            <Dialog
                open={historyDialog.open}
                onClose={() => setHistoryDialog({ open: false, task: null, history: [], loading: false })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Completion History
                </DialogTitle>
                <DialogContent>
                    {historyDialog.task && (
                        <Box mb={2}>
                            <Typography variant="h6">{historyDialog.task.name}</Typography>
                            <Chip
                                size="small"
                                label={historyDialog.task.isds_reference}
                                color="primary"
                                sx={{ mt: 1 }}
                            />
                        </Box>
                    )}

                    {historyDialog.loading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : historyDialog.history.length === 0 ? (
                        <Alert severity="info">No completion history found for this task.</Alert>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Completed By</TableCell>
                                        <TableCell>Notes</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historyDialog.history.map((record, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{formatDate(record.completed_at)}</TableCell>
                                            <TableCell>{record.completed_by_username}</TableCell>
                                            <TableCell>
                                                {record.notes ? (
                                                    <Typography variant="body2">{record.notes}</Typography>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                        No notes
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryDialog({ open: false, task: null, history: [], loading: false })}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
